import { useQuery } from '@tanstack/react-query';
import type { PageResponse } from '@models/api';
import {
  getEsimInventoryEvents,
  type EsimInventoryEventResponse,
} from '@apis/esim';

/**
 * Fetch event (history) list for a specific eSIM inventory.
 *
 * - Uses React Query to cache inventory event history.
 * - Each inventory has its own isolated cache entry.
 * - Refetch on window focus is disabled to avoid unexpected UI updates
 *   while the user is reviewing history or editing related data.
 */
export const useInventoryEvents = (esimInventoryId: number) => {
  /**
   * Only enable the query when a valid inventory ID is provided.
   * Inventory IDs must be positive integers.
   */
  const enabled = Number.isFinite(esimInventoryId) && esimInventoryId > 0;

  return useQuery<PageResponse<EsimInventoryEventResponse>, Error>({
    /**
     * Query key includes inventory ID to prevent cache collisions
     * between different inventory detail pages.
     */
    queryKey: ['inventoryEvents', esimInventoryId],

    /**
     * API call to retrieve inventory event history.
     */
    queryFn: () => getEsimInventoryEvents(esimInventoryId),

    /**
     * Prevents unnecessary API calls when ID is invalid.
     */
    enabled,

    /**
     * Event history is relatively stable.
     * Cache is considered fresh for 30 seconds to reduce refetch noise.
     */
    staleTime: 30 * 1000,

    /**
     * Disable refetch on window focus to keep
     * event ordering and UI state stable.
     */
    refetchOnWindowFocus: false,
  });
};
